import React, { useState, useRef, forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import { SingleInput } from './SingleInput';
import { SingleLineEntry } from './singleLineEntry';
import * as Reorder from '../../../../components/dnd/Reorder';
import { useMergePendingItems, useMergePendingItemsHandle } from '../../../../hooks/useMergePendingItems';

interface Props {
  interests: Array<{ interest: string }>;
  updateInterests: (interests: Array<{ interest: string }>) => void;
}

export default forwardRef<{ mergePendingItems: () => Array<{ interest: string }> }, Props>(
  function FormInterests({ interests, updateInterests }, ref) {
    const [editableIndex, setEditableIndex] = useState(-1);
    const inputRef = useRef<{ getCurrentValue: () => string; clear: () => void }>(null);

    const { pendingItems, mergePendingItems, addToPending, clearPending } = useMergePendingItems<{ interest: string }, string>({
      items: interests,
      updateItems: updateInterests,
      inputRef,
      transformItem: (item) => ({ interest: item as string }),
      mergeOrder: 'prepend',
    });

    useMergePendingItemsHandle(ref, mergePendingItems);

    const isValidKeyPoint = (text: string) => text?.trim()?.length;

    const handleAddToPending = (interest: string) => {
      if (isValidKeyPoint(interest)) {
        addToPending(interest);
      }
    };

    const addSingleInterest = (interest: string) => {
      if (isValidKeyPoint(interest)) {
        const allItems = [...pendingItems, interest];
        updateInterests([...allItems.map(item => ({ interest: item })), ...interests]);
        clearPending();
      }
      setEditableIndex(-1);
    };

  const updateSingleInterest = (interest: string, index: number) => {
    const tempList = [...interests];
    tempList[index] = { interest };
    updateInterests([...tempList]);
    setEditableIndex(-1);
  };

  const removeSingleInterest = (index: number) => {
    const tempList = [...interests];
    tempList.splice(index, 1);
    updateInterests([...tempList]);
  };

  const props = { label: 'Interest' };
  return (
    <Box className="flex flex-col gap-2 w-full">
      <SingleInput 
        ref={inputRef}
        {...props} 
        onTextChange={addSingleInterest}
        onAddToPending={handleAddToPending}
      />

      {pendingItems.length > 0 && (
        <Box className="flex flex-col gap-2 w-full mb-2">
          {pendingItems.map((item, index) => (
            <Box key={`pending-${index}`} className="p-2 border border-dashed border-border-token rounded-lg bg-surface-2">
              <Typography className="text-text-1">{item}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Reorder.Root options={interests} onUpdateOrder={updateInterests}>
        {interests.map((item, index) => {
          const interestText = typeof item === 'string' ? item : item.interest;
          if (editableIndex === index) {
            return (
              <SingleInput
                {...props}
                key={`interest-${interestText}-${index}`}
                text={interestText}
                onTextChange={(text) => updateSingleInterest(text, index)}
                isEditMode={true}
              />
            );
          }

          return (
            <SingleLineEntry
              text={interestText}
              index={index}
              key={`interest-${interestText}-${index}`}
              editText={() => setEditableIndex(index)}
              removeText={() => removeSingleInterest(index)}
            />
          );
        })}
      </Reorder.Root>
    </Box>
  );
});
